import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, input, output, viewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject, debounceTime, map, switchMap, tap } from 'rxjs';

import { SearchRequestModel } from '~app/models/requests/search-request.model';
import { BaseControlValueAccessor } from '~core/abstractions/base-control-value-accessor';
import { Identifyable } from '~core/abstractions/identifyable';
import { ErrorTooltipDirective } from '~core/directives/error-tooltip.directive';
import { OptionalType } from '~shared/core/types/optional.type';
import { QuickSearchConfigModel } from './models/quick-search-config.model';

@Component({
    selector: 'arc-quick-search',
    templateUrl: './quick-search.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuickSearchComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatFormField,
        ErrorTooltipDirective,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        MatSuffix,
        MatIconButton,
        MatIcon,
        MatProgressSpinner,
        TranslocoPipe
    ]
})
export class QuickSearchComponent<TList extends Identifyable<TId>, TId = number> extends BaseControlValueAccessor<TId> {
    readonly autocomplete = viewChild.required(MatAutocomplete);
    readonly config = input.required<QuickSearchConfigModel<TList, TId>>();
    readonly optionSelected = output<TList>();
    readonly inputChanged = output<string>();

    isLoading = false;
    isInvalidInput = false;
    options: TList[] = [];

    protected readonly _debounceTimeMs = 250;
    protected readonly _searchResultSize = 50;

    private currentSelection?: TList;

    private readonly internalSearchSubject = new Subject<string>();
    private readonly matDialog = inject(MatDialog);
    private readonly changeDetectionRef = inject(ChangeDetectorRef);

    constructor() {
        super();

        this.internalSearchSubject
            .pipe(
                tap(() => {
                    this.options = [];
                    this.isLoading = true;
                    this.isInvalidInput = true;
                }),
                debounceTime(this._debounceTimeMs),
                switchMap(searchText =>
                    this.config()
                        .store.search(
                            new SearchRequestModel({
                                searchText,
                                pageSize: this._searchResultSize,
                                filters: this.config().filters,
                                sortings: this.config().sortings
                            })
                        )
                        .pipe(map(response => response.value?.records ?? []))
                )
            )
            .subscribe(options => {
                this.options = options;
                this.isLoading = false;
            });
    }

    override writeValue(value?: TId): void {
        if (!value) {
            super.writeValue(undefined);
            this.select(undefined, true);
            this.options = [];
            return;
        }

        this.isLoading = true;
        this.config()
            .store.getListModel(value)
            .subscribe({
                next: result => {
                    super.writeValue(result.value?.id);

                    if (!!result.value) {
                        const existingIndex = this.options.findIndex(opt => opt.id === result.value!.id);

                        if (existingIndex === -1) {
                            this.options = [result.value, ...this.options];
                        }
                    }

                    this.select(result.value, true);
                },
                complete: () => {
                    this.isLoading = false;
                    this.changeDetectionRef.markForCheck();
                }
            });
    }

    input(): void {
        const value = this.internalControl.value;

        if (typeof value === 'string') {
            if (value.length > 0) {
                this.internalSearchSubject.next(value);
                this.inputChanged.emit(value);
            } else {
                this.inputChanged.emit('');
                this.options = [];
                this.select(undefined);
            }
        }
    }

    onBlur(): void {
        setTimeout(() => {
            if (this.isInvalidInput) {
                this.select(this.currentSelection);
            }
        }, 200);
    }

    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        this.isInvalidInput = false;
        const value = event.option.value as TList;

        this.select(value);

        this.options = [value];
    }

    async openDataSelectionDialog(event: MouseEvent): Promise<void> {
        event.stopPropagation();

        if (this.isDisabled) {
            return;
        }

        const { DataSelectionDialogComponent } = await import('~components/dialogs/data-selection-dialog/data-selection-dialog.component');
        const dialogRef = this.matDialog.open(DataSelectionDialogComponent, {
            data: this.config().dataSelectionDialogConfig,
            width: '800px',
            maxWidth: '98vw',
            height: '800px',
            maxHeight: '98svh'
        });

        dialogRef.afterClosed().subscribe((result?: TList[]) => {
            if (this.isDisabled) {
                return;
            }

            this.isInvalidInput = false;

            if (!!result && result.length > 0) {
                this.select(result[0]);
            }

            this.markAsTouched();
        });
    }

    private select(option: OptionalType<TList>, isSetInternally = false): void {
        this.currentSelection = option;
        this.internalControl.setValue(option);
        this.isInvalidInput = false;

        this.changeDetectionRef.markForCheck();

        if (!isSetInternally) {
            this.valueChanged(option?.id);

            if (!!option) {
                this.optionSelected.emit(option);
            }
        }
    }
}
